import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Loader2, Sparkles, Send, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp, Lock } from "lucide-react";
import { toast } from "sonner";
import { useVoiceGuide } from "@/contexts/VoiceAssistantContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { full_name: "", birth_date: "", location: "", children: "", partner: "", questions: "", extra: "" };
const inputCls = "w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white placeholder:text-neutral-500 focus:outline-none focus:border-[#d4af37]/60";

const countQuestions = (t) => {
  const lines = (t || "").split("\n").map((l) => l.trim()).filter(Boolean);
  const n = lines.filter((l) => /^(\d+[).\-:]|[-•*▪✔✓]|¿)/.test(l) || l.endsWith("?")).length;
  return Math.max(n, ((t || "").match(/\?/g) || []).length);
};

const Guide = ({ text }) => {
  const [open, setOpen] = useState(true);
  return (
    <div className="rounded-2xl border border-[#d4af37]/30 bg-black/30 overflow-hidden" data-testid="reading-form-guide">
      <button type="button" onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left text-[#d4af37] font-semibold text-sm">
        Guía para tu lectura {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>
      {open && (
        <div className="px-4 pb-4 text-sm text-neutral-300 space-y-1 whitespace-pre-wrap leading-relaxed">
          {text.split("\n").map((ln, i) => {
            const clean = ln.replace(/^#+\s*/, "").replace(/\*\*/g, "");
            const isH = /^#/.test(ln);
            return <div key={i} className={isH ? "text-white font-semibold mt-3" : ""}>{clean}</div>;
          })}
        </div>
      )}
    </div>
  );
};

export default function ReadingFormPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  useVoiceGuide("rform:ready", "Esta es la guía para tu lectura. Escribe tu nombre completo, tu fecha de nacimiento y la ciudad donde vives. Si tu consulta involucra a tu pareja o a tus hijos, agrega sus nombres. Después escribe mínimo seis preguntas claras, una por línea. Entre más específico seas, más precisa será tu lectura.", !!data && !sent);
 useVoiceGuide("rform:done", "Tu información fue enviada. Cristopher la revisará antes de tu llamada. Gracias por tu confianza.", sent);
  const timer = useRef(null);

  useEffect(() => {
    axios.get(`${API}/reading-form/${token}`)
      .then((r) => { setData(r.data); setF({ ...EMPTY, ...(r.data.fields || {}) }); setSent(r.data.status === "submitted"); })
      .catch((e) => setErr(e?.response?.data?.detail || "Enlace no válido"));
  }, [token]);

  const update = (k, v) => {
    const next = { ...f, [k]: v };
    setF(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => axios.post(`${API}/reading-form/${token}/draft`, next).catch(() => null), 900);
  };

  const qn = countQuestions(f.questions);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/reading-form/${token}/submit`, f);
      setSent(true);
      toast.success("¡Información enviada! Cristopher la revisará antes de tu llamada.");
    } catch (er) {
      toast.error(er?.response?.data?.detail || "No se pudo enviar");
    } finally { setBusy(false); }
  };

  if (err) {
    return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center p-6"><div className="glass-dark rounded-2xl p-8 text-center text-neutral-300 max-w-md" data-testid="reading-form-error"><AlertTriangle className="w-8 h-8 text-amber-400 mx-auto mb-3" />{err}</div></div>;
  }
  if (!data) return <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#d4af37]" /></div>;

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <div className="max-w-2xl mx-auto px-4 py-10 space-y-6">
        <header className="space-y-2">
          <div className="inline-flex items-center gap-2 text-[#d4af37] text-xs uppercase tracking-[0.3em]"><Sparkles className="w-4 h-4" /> CrisAngel Astral</div>
          <h1 className="font-serif text-3xl sm:text-4xl" data-testid="reading-form-title">Hola {data.client_name?.split(" ")[0] || ""}, prepara tu lectura</h1>
          <p className="text-sm text-neutral-400">Servicio: <span className="text-neutral-200">{data.service_name}</span> · <Lock className="inline w-3 h-3" /> Toda tu información es 100% confidencial.</p>
        </header>

        <Guide text={data.guide} />

        {sent ? (
          <div className="rounded-2xl border border-emerald-500/40 bg-emerald-500/10 p-6 text-center space-y-2" data-testid="reading-form-sent">
            <CheckCircle2 className="w-8 h-8 text-emerald-300 mx-auto" />
            <div className="font-semibold">Información enviada</div>
            <p className="text-sm text-emerald-100/80">Cristopher ya la tiene. Puedes corregir algo y volver a enviar si lo necesitas.</p>
            <button type="button" data-testid="reading-form-edit" onClick={() => setSent(false)} className="text-xs underline text-emerald-200">Editar mis respuestas</button>
          </div>
        ) : (
          <form onSubmit={submit} className="glass-dark rounded-2xl p-5 sm:p-6 space-y-4" data-testid="reading-form">
            <h2 className="text-base md:text-lg font-semibold text-[#d4af37]">1. Datos personales</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="rf-full-name" className={inputCls} placeholder="Nombre completo *" value={f.full_name} onChange={(e) => update("full_name", e.target.value)} />
              <input data-testid="rf-birth-date" className={inputCls} placeholder="Fecha de nacimiento (día/mes/año) *" value={f.birth_date} onChange={(e) => update("birth_date", e.target.value)} />
              <input data-testid="rf-location" className={`${inputCls} sm:col-span-2`} placeholder="Ciudad y país donde vives *" value={f.location} onChange={(e) => update("location", e.target.value)} />
            </div>
            <h2 className="text-base md:text-lg font-semibold text-[#d4af37]">2. Información familiar (si aplica)</h2>
            <div className="grid sm:grid-cols-2 gap-3">
              <input data-testid="rf-children" className={inputCls} placeholder="Hijos: nombres y edades" value={f.children} onChange={(e) => update("children", e.target.value)} />
              <input data-testid="rf-partner" className={inputCls} placeholder="Pareja / ex pareja: nombre y fecha de nacimiento" value={f.partner} onChange={(e) => update("partner", e.target.value)} />
            </div>
            <div className="flex items-center justify-between">
              <h2 className="text-base md:text-lg font-semibold text-[#d4af37]">3. Tus preguntas (mínimo {data.min_questions})</h2>
              <span data-testid="rf-question-count" className={`text-xs px-2 py-0.5 rounded-full ${qn >= data.min_questions ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`}>{qn}/{data.min_questions} preguntas</span>
            </div>
            <textarea data-testid="rf-questions" rows={9} className={inputCls} placeholder={"Escribe una pregunta por línea, terminada en «?».\n1. ¿Cómo evolucionará mi relación con ... en los próximos meses?\n2. ¿Debo aceptar la oferta de trabajo o esperar?\n3. ..."} value={f.questions} onChange={(e) => update("questions", e.target.value)} />
            <textarea data-testid="rf-extra" rows={4} className={inputCls} placeholder="Contexto adicional que quieras compartir (situación actual, nombres de personas involucradas, lo que sientes)…" value={f.extra} onChange={(e) => update("extra", e.target.value)} />
            <p className="text-[11px] text-neutral-500">⚠️ Si la información está incompleta o no cumples el mínimo de preguntas, la lectura será cancelada sin reembolso.</p>
            <button type="submit" data-testid="rf-submit" disabled={busy} className="w-full rounded-full bg-[#d4af37] text-black font-semibold py-3 hover:bg-[#e6c35a] disabled:opacity-50 flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Enviar mi información a Cristopher
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
